import { InputAdornment } from '@mui/material'
import { useTranslate } from 'ra-core'
import { useCallback, useMemo } from 'react'
import { NumberInput, number, useSourceContext, type NumberInputProps, type Validator } from 'react-admin'
import { useWatch } from 'react-hook-form'

import { useHelperText } from '../resources/common/forms'

type FormatValue = string | number | null | undefined

export type AdvancedNumberInputProps = Omit<NumberInputProps, 'validate' | 'format'> & {
  min?: number
  max?: number
  unit?: string
  validate?: Validator | Validator[]
  format?: (value: FormatValue) => FormatValue
}

const isEmpty = (value: unknown): value is null | undefined | '' => value == null || value === ''

const AdvancedNumberInput = ({ min, max, unit, validate, source, format, ...props }: AdvancedNumberInputProps) => {
  const sourceContext = useSourceContext()
  const finalSource = sourceContext.getSource(source)
  const value = useWatch({ name: finalSource })
  const translate = useTranslate()
  const helperText = useHelperText(source)

  const toDisplayValue = useCallback((inputValue: FormatValue) => (format ? format(inputValue) : inputValue), [format])

  const validators = useMemo<Validator[]>(() => {
    const baseValidators = Array.isArray(validate) ? validate : validate ? [validate] : []

    const boundValidator = (
      bound: number | undefined,
      isValid: (value: number, bound: number) => boolean,
      translationKey: 'ra.validation.minValue' | 'ra.validation.maxValue',
    ): Validator | null =>
      isEmpty(bound)
        ? null
        : (inputValue) => {
            if (isEmpty(inputValue)) return undefined
            const displayValue = toDisplayValue(inputValue as FormatValue)
            if (typeof displayValue !== 'number' || Number.isNaN(displayValue)) {
              return undefined
            }

            return isValid(displayValue, bound)
              ? undefined
              : translate(translationKey, {
                  [translationKey === 'ra.validation.minValue' ? 'min' : 'max']: bound,
                })
          }

    return [
      number('mymove.validation.number.invalid'),
      ...baseValidators,
      ...[
        boundValidator(min, (currentValue, currentBound) => currentValue >= currentBound, 'ra.validation.minValue'),
        boundValidator(max, (currentValue, currentBound) => currentValue <= currentBound, 'ra.validation.maxValue'),
      ].filter((validator): validator is Validator => validator !== null),
    ]
  }, [validate, min, max, toDisplayValue, translate])

  return (
    <NumberInput
      source={source}
      min={min}
      max={max}
      validate={validators}
      format={format}
      helperText={helperText ? <span style={{ whiteSpace: 'pre-line' }}>{helperText}</span> : undefined}
      slotProps={
        unit
          ? {
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {translate(unit, { smart_count: toDisplayValue(value as FormatValue) || 1 })}
                  </InputAdornment>
                ),
              },
            }
          : undefined
      }
      {...props}
    />
  )
}

export default AdvancedNumberInput
